import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile, RedSAL } from '../../../types/red'
import { STATO_SAL_CONFIG, EUR } from '../../../constants/red'
import { redApi } from '../../../services/api'

const TabSAL: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const [salList, setSalList] = useState<RedSAL[]>([])
  const [loading, setLoading] = useState(true)
  const [nuovoSal, setNuovoSal] = useState({ numero_sal: '', importo_sal: '', data_emissione_imeco: '' })

  const loadSal = () => {
    redApi.sal.listByImmobile(immobile.id).then(res => {
      setSalList(res.data)
      setLoading(false)
    })
  }

  useEffect(loadSal, [immobile.id])

  const handleCreateSal = async () => {
    if (!nuovoSal.numero_sal || !nuovoSal.importo_sal || !nuovoSal.data_emissione_imeco) {
      toast.error('Tutti i campi sono obbligatori')
      return
    }
    await redApi.sal.create({
      immobile_id: immobile.id,
      numero_sal: parseInt(nuovoSal.numero_sal, 10),
      importo_sal: parseFloat(nuovoSal.importo_sal),
      data_emissione_imeco: nuovoSal.data_emissione_imeco,
    })
    toast.success('SAL creato')
    setNuovoSal({ numero_sal: '', importo_sal: '', data_emissione_imeco: '' })
    loadSal()
    onUpdate()
  }

  const handleBenestareDL = async (sal: RedSAL) => {
    const data_benestare_dl = prompt('Data benestare DL (YYYY-MM-DD):')
    if (!data_benestare_dl) return
    const note_dl = prompt('Note DL (opzionale):') || undefined
    await redApi.sal.benestareDL(sal.id, { data_benestare_dl, note_dl })
    loadSal()
    onUpdate()
  }

  const handleRichiestaUSR = async (sal: RedSAL) => {
    if (!immobile.usr_quota_percentuale || !sal.importo_sal) return
    const importo = (sal.importo_sal * immobile.usr_quota_percentuale / 100).toFixed(2)
    const data_richiesta_usr = new Date().toISOString().split('T')[0]
    await redApi.sal.richiestaUSR(sal.id, {
      importo_richiesto_usr: parseFloat(importo),
      data_richiesta_usr,
    })
    try {
      await redApi.documenti.generaRichiestaSal(sal.id)
      toast.success('Richiesta USR registrata e PDF generato')
    } catch {
      toast.success('Richiesta USR registrata (PDF non generato)')
    }
    loadSal()
  }

  const handleErogazioneUSR = async (sal: RedSAL) => {
    const importo_str = prompt('Importo erogato da USR (€):')
    if (!importo_str) return
    const data_erogazione_usr = prompt('Data erogazione (YYYY-MM-DD):') || new Date().toISOString().split('T')[0]
    const riferimento = prompt('Riferimento bonifico (opzionale):') || undefined
    await redApi.sal.erogazioneUSR(sal.id, {
      importo_erogato_usr: parseFloat(importo_str),
      data_erogazione_usr,
      riferimento_erogazione_usr: riferimento,
    })
    loadSal()
    onUpdate()
  }

  if (loading) return <div className="text-gray-400">Caricamento SAL...</div>

  const totaleImeco = salList.reduce((s, sal) => s + (sal.importo_sal || 0), 0)
  const totaleErogato = salList.reduce((s, sal) => s + (sal.importo_erogato_usr || 0), 0)

  return (
    <div>
      <div className="card p-4 mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">Nuovo SAL</h2>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="label">N° SAL</label>
            <input type="number" className="input w-24" value={nuovoSal.numero_sal} onChange={e => setNuovoSal({ ...nuovoSal, numero_sal: e.target.value })} />
          </div>
          <div>
            <label className="label">Importo SAL (€)</label>
            <input type="number" className="input" value={nuovoSal.importo_sal} onChange={e => setNuovoSal({ ...nuovoSal, importo_sal: e.target.value })} />
          </div>
          <div>
            <label className="label">Data emissione IMECO</label>
            <input type="date" className="input" value={nuovoSal.data_emissione_imeco} onChange={e => setNuovoSal({ ...nuovoSal, data_emissione_imeco: e.target.value })} />
          </div>
          <button onClick={handleCreateSal} className="btn-primary">Crea SAL</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">Totale SAL IMECO</div>
          <div className="text-xl font-bold text-gray-900">{EUR.format(totaleImeco)}</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">Totale Erogato USR</div>
          <div className="text-xl font-bold text-blue-700">{EUR.format(totaleErogato)}</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-sm text-gray-500">Residuo USR</div>
          <div className="text-xl font-bold text-orange-700">
            {EUR.format((immobile.calcoli?.finanziamento_usr_totale || 0) - totaleErogato)}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {salList.map(sal => {
          const statoConfig = STATO_SAL_CONFIG[sal.stato]
          return (
            <div key={sal.id} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">SAL N° {sal.numero_sal}</h3>
                <span className="text-sm font-medium px-3 py-1 rounded-full bg-gray-100" style={{ color: statoConfig.color }}>
                  {statoConfig.label}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                <div>
                  <div className="text-gray-400 text-xs">Importo IMECO</div>
                  <div className="font-semibold">{sal.importo_sal ? EUR.format(sal.importo_sal) : '—'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Quota USR ({immobile.usr_quota_percentuale}%)</div>
                  <div className="font-semibold text-blue-700">{sal.importo_richiesto_usr ? EUR.format(sal.importo_richiesto_usr) : '—'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Quota Delta</div>
                  <div className="font-semibold text-orange-700">{sal.importo_quota_delta ? EUR.format(sal.importo_quota_delta) : '—'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">Erogato USR</div>
                  <div className="font-semibold text-green-700">{sal.importo_erogato_usr ? EUR.format(sal.importo_erogato_usr) : '—'}</div>
                </div>
              </div>

              <div className="flex gap-2">
                {sal.stato === 'in_attesa_dl' && (
                  <button onClick={() => handleBenestareDL(sal)} className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600">
                    Registra Benestare DL
                  </button>
                )}
                {sal.stato === 'in_attesa_usr' && !sal.data_richiesta_usr && (
                  <button onClick={() => handleRichiestaUSR(sal)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Invia Richiesta USR + PDF
                  </button>
                )}
                {sal.stato === 'in_attesa_usr' && sal.data_richiesta_usr && !sal.importo_erogato_usr && (
                  <button onClick={() => handleErogazioneUSR(sal)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700">
                    Registra Erogazione USR
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!confirm('Eliminare questo SAL?')) return
                    try {
                      await redApi.sal.delete(sal.id)
                      toast.success('SAL eliminato')
                      loadSal()
                      onUpdate()
                    } catch {
                      toast.error('Errore eliminazione')
                    }
                  }}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  🗑 Elimina
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {salList.length === 0 && (
        <div className="text-center py-10 text-gray-400">Nessun SAL registrato.</div>
      )}
    </div>
  )
}

export default TabSAL
